import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { useErrorHandler } from '@/hooks/useErrorHandler';
import { ErrorCategory } from '@/utils/errorHandler';
import { useMemo } from 'react';
import { BillAssistInsightView, SmartCardData } from '@/types/billAssistTypes';

export interface BillAssistMetrics {
  total_items: number;
  total_revenue_at_risk: number;
  avg_confidence: number;
  critical_count: number;
  high_count: number;
  medium_count: number;
  low_count: number;
}

export interface BillAssistData {
  insights: BillAssistInsightView[];
  metrics: BillAssistMetrics;
  smartCards: SmartCardData[];
}

/**
 * Hook to fetch and process Bill Assist data from the insights view
 */
export const useBillAssistData = () => {
  const { user } = useAuth();
  const errorHandler = useErrorHandler('useBillAssistData');
  
  const organizationId = useMemo(() => {
    return user?.user_metadata?.organization_id as string | undefined;
  }, [user?.user_metadata?.organization_id]);

  return useQuery({
    queryKey: ['billAssistData', organizationId] as const,
    queryFn: async (): Promise<BillAssistData> => {
      if (!organizationId) {
        throw errorHandler.handleError(
          new Error('No organization ID found'),
          'fetchBillAssistData',
          { userId: user?.id },
          false
        );
      }

      try {
        // Get insights from the view
        const { data: insights, error: insightsError } = await supabase
          .from('bill_assist_insights')
          .select('*')
          .eq('organization_id', organizationId)
          .order('financial_impact', { ascending: false });

        if (insightsError) throw insightsError;

        // Get aggregated metrics using the RPC function
        const { data: metricsData, error: metricsError } = await supabase
          .rpc('get_bill_assist_metrics', {
            org_id: organizationId
          });

        if (metricsError) throw metricsError;

        const metrics: BillAssistMetrics = metricsData?.[0] || {
          total_items: 0,
          total_revenue_at_risk: 0,
          avg_confidence: 0,
          critical_count: 0,
          high_count: 0,
          medium_count: 0,
          low_count: 0
        };

        // Process insights into smart cards
        const smartCards = processInsightsIntoCards(insights || []);

        return {
          insights: insights || [],
          metrics,
          smartCards
        };
      } catch (error) {
        throw errorHandler.handleError(
          error,
          'fetchBillAssistData',
          { 
            userId: user?.id,
            organizationId,
            category: ErrorCategory.DATABASE
          },
          false
        );
      }
    },
    staleTime: 2 * 60 * 1000, // Consider data fresh for 2 minutes
    gcTime: 5 * 60 * 1000, // Keep unused data in cache for 5 minutes
    retry: (failureCount, error) => {
      // Don't retry on auth errors
      if (error?.message?.includes('organization_id')) return false;
      return failureCount < 2;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    refetchOnWindowFocus: false,
    enabled: !!organizationId,
  });
};

/**
 * Process raw insights into smart card data
 */
function processInsightsIntoCards(insights: BillAssistInsightView[]): SmartCardData[] {
  // Group insights by category
  const groupedInsights = insights.reduce((acc, insight) => {
    const category = insight.category;
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(insight);
    return acc;
  }, {} as Record<string, BillAssistInsightView[]>);

  // Always create all 4 core categories - process real data where available, zeros where not
  const smartCards: SmartCardData[] = [];

  // 1. Process Overdue Invoices (real data if available)
  const overdueInvoices = groupedInsights['overdue_invoices'] || [];
  if (overdueInvoices.length > 0) {
    const totalImpact = overdueInvoices.reduce((sum, item) => sum + item.financial_impact, 0);
    const avgConfidence = overdueInvoices.reduce((sum, item) => sum + item.confidence_score, 0) / overdueInvoices.length;
    const criticalCount = overdueInvoices.filter(item => item.priority === 'critical').length;
    const highCount = overdueInvoices.filter(item => item.priority === 'high').length;
    
    let priority: 'critical' | 'high' | 'medium' | 'low' = 'medium';
    if (criticalCount > 0) priority = 'critical';
    else if (highCount > 2) priority = 'high';
    else if (overdueInvoices.length > 5) priority = 'high';

    smartCards.push({
      title: "Overdue Invoices",
      count: overdueInvoices.length,
      impact: formatCurrency(totalImpact / 100),
      priority,
      suggestion: generateOverdueSuggestion(overdueInvoices),
      confidence: Math.round(avgConfidence * 100) / 100,
      icon: "AlertTriangle",
      category: 'overdue_invoices',
      actions: [{
        id: 'view-overdue',
        action_type: 'manual_review',
        title: 'Review Overdue Invoices',
        description: 'Navigate to invoices page with overdue filter',
        confidence: 0.95,
        success_rate: 0.85,
        estimated_effort: 'medium',
        automation_available: false,
        expected_outcome: 'Review and follow up on overdue payments'
      }]
    });
  } else {
    // Show zero data for overdue invoices
    smartCards.push({
      title: "Overdue Invoices",
      count: 0,
      impact: "$0.00",
      priority: 'low',
      suggestion: "All invoices are current. Great job!",
      confidence: 0.94,
      icon: "AlertTriangle",
      category: 'overdue_invoices',
      actions: [{
        id: 'view-overdue',
        action_type: 'manual_review',
        title: 'Review All Invoices',
        description: 'Navigate to invoices page',
        confidence: 0.95,
        success_rate: 0.85,
        estimated_effort: 'low',
        automation_available: false,
        expected_outcome: 'Monitor invoice status'
      }]
    });
  }

  // 2. Process Payment Failures (real data if available)
  const paymentFailures = groupedInsights['payment_failures'] || [];
  if (paymentFailures.length > 0) {
    const totalImpact = paymentFailures.reduce((sum, item) => sum + item.financial_impact, 0);
    const avgConfidence = paymentFailures.reduce((sum, item) => sum + item.confidence_score, 0) / paymentFailures.length;

    smartCards.push({
      title: "Payment Failures",
      count: paymentFailures.length,
      impact: formatCurrency(totalImpact / 100),
      priority: paymentFailures.length > 2 ? 'critical' : 'high',
      suggestion: "Call families - payment methods likely expired or insufficient funds",
      confidence: Math.round(avgConfidence * 100) / 100,
      icon: "CreditCard",
      category: 'payment_failures',
      actions: [{
        id: 'review-failures',
        action_type: 'personal_call',
        title: 'Contact Families',
        description: 'Review failed payments and contact families',
        confidence: 0.89,
        success_rate: 0.75,
        estimated_effort: 'high',
        automation_available: false,
        expected_outcome: 'Resolve payment method issues'
      }]
    });
  } else {
    // Show zero data for payment failures
    smartCards.push({
      title: "Payment Failures",
      count: 0,
      impact: "$0.00",
      priority: 'low',
      suggestion: "No recent payment failures detected",
      confidence: 0.89,
      icon: "CreditCard",
      category: 'payment_failures',
      actions: [{
        id: 'review-failures',
        action_type: 'manual_review',
        title: 'Review Payment History',
        description: 'Check recent payment activity',
        confidence: 0.89,
        success_rate: 0.75,
        estimated_effort: 'medium',
        automation_available: false,
        expected_outcome: 'Monitor payment health'
      }]
    });
  }

  // 3. Expiring Cards (always zero for now - future data source)
  smartCards.push({
    title: "Expiring Cards (Auto-Billing)",
    count: 0,
    impact: "$0.00",
    priority: 'low',
    suggestion: "No expiring payment methods detected for auto-billing families",
    confidence: 0.95,
    icon: "Clock",
    category: 'expiring_cards',
    actions: [{
      id: 'review-expiring',
      action_type: 'manual_review',
      title: 'Review Auto-Billing Families',
      description: 'Check payment method expiration dates',
      confidence: 0.95,
      success_rate: 0.80,
      estimated_effort: 'medium',
      automation_available: true,
      expected_outcome: 'Proactive payment method updates'
    }]
  });

  // 4. High-Value Family Risks (always zero for now - future data source)
  smartCards.push({
    title: "High-Value Family Risks",
    count: 0,
    impact: "$0.00",
    priority: 'low',
    suggestion: "No high-value families at risk detected",
    confidence: 0.87,
    icon: "Users",
    category: 'high_value_risks',
    actions: [{
      id: 'review-high-value',
      action_type: 'manual_review',
      title: 'Review Family Risk Scores',
      description: 'Analyze payment patterns for high-value families',
      confidence: 0.87,
      success_rate: 0.70,
      estimated_effort: 'medium',
      automation_available: false,
      expected_outcome: 'Proactive outreach to valuable families'
    }]
  });

  // Sort cards by priority and impact for consistent display order
  return smartCards.sort((a, b) => {
    const priorityOrder = { 'critical': 4, 'high': 3, 'medium': 2, 'low': 1 };
    const aPriority = priorityOrder[a.priority];
    const bPriority = priorityOrder[b.priority];
    
    if (aPriority !== bPriority) {
      return bPriority - aPriority; // Higher priority first
    }
    
    // If same priority, sort by count (higher count first)
    return b.count - a.count;
  });
}

/**
 * Generate contextual suggestions for overdue invoices
 */
function generateOverdueSuggestion(overdueInvoices: BillAssistInsightView[]): string {
  const criticalCount = overdueInvoices.filter(item => item.priority === 'critical').length;
  const recentCount = overdueInvoices.filter(item => (item.days_overdue || 0) <= 7).length;
  
  if (criticalCount > 0) {
    return `${criticalCount} critically overdue payments need immediate attention`;
  }
  
  if (recentCount > 0) {
    return `Auto-send reminder emails to ${recentCount} families with payments 1-7 days overdue`;
  }
  
  return "Review and follow up on all overdue payments";
}

/**
 * Format currency consistently
 */
function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount);
}